import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import LegalFooter from "./LegalFooter";
import {
  LayoutGrid,
  CandlestickChart,
  Bot,
  Activity,
  Wallet,
  History,
  Users,
  LogOut,
  Zap,
  Settings as SettingsIcon,
  Sparkles,
  AlertOctagon,
} from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Overview", icon: LayoutGrid, testid: "nav-overview" },
  { to: "/markets", label: "Markets", icon: CandlestickChart, testid: "nav-markets" },
  { to: "/signals", label: "Alpha Signals", icon: Sparkles, testid: "nav-signals" },
  { to: "/bots", label: "Bots", icon: Bot, testid: "nav-bots" },
  { to: "/trades", label: "Active Trades", icon: Activity, testid: "nav-trades" },
  { to: "/portfolio", label: "Portfolio", icon: Wallet, testid: "nav-portfolio" },
  { to: "/history", label: "History", icon: History, testid: "nav-history" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, mode, setMode, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && user.role === "admin";
  const live = mode === "live";
  const [panicking, setPanicking] = useState(false);

  async function panicStopAll() {
    if (!window.confirm("PANIC STOP — immediately stop ALL running bots and cancel ALL open exchange orders. Continue?")) return;
    setPanicking(true);
    try {
      const r = await api.post("/panic/stop-all");
      toast.success(`Stopped ${r.data.bots_stopped} bot(s) · cancelled ${r.data.open_orders_cancelled} open order(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Panic stop failed");
    } finally {
      setPanicking(false);
    }
  }

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="w-60 border-r border-border flex flex-col" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-border flex items-center gap-2">
          <div className="w-7 h-7 bg-[#007AFF] flex items-center justify-center" data-testid="brand-logo">
            <Zap size={16} className="text-white" />
          </div>
          <div>
            <div className="font-[Chivo] text-sm tracking-tight">MICRO/TRADE</div>
            <div className="label-xs">Execution Engine</div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-[#1a1a1a] text-white border-l-2 border-[#007AFF]"
                    : "text-neutral-400 hover:text-white hover:bg-[#141414]"
                }`
              }
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}

          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-[#1a1a1a] text-white border-l-2 border-[#FFB300]"
                    : "text-neutral-400 hover:text-white hover:bg-[#141414]"
                }`
              }
            >
              <Users size={15} />
              Admin
            </NavLink>
          )}
        </nav>

        <div className="p-4 border-t border-border space-y-3">
          <div className="text-xs text-neutral-500">Signed in as</div>
          <div className="text-sm truncate" data-testid="user-email">{user?.email}</div>
          <Button
            data-testid="logout-button"
            variant="outline"
            className="w-full border-border bg-transparent hover:bg-[#141414] text-neutral-300"
            onClick={async () => { await logout(); navigate("/login"); }}
          >
            <LogOut size={14} className="mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border flex items-center justify-between px-6">
          <div className="flex items-center gap-6">
            <div>
              <div className="label-xs">Trading Mode</div>
            </div>
            <div className="flex items-center gap-3" data-testid="mode-toggle-container">
              <span className={`text-xs font-[Chivo] tracking-wider ${!live ? "text-white" : "text-neutral-500"}`}>PAPER</span>
              <Switch
                data-testid="mode-toggle"
                checked={live}
                onCheckedChange={(c) => setMode(c ? "live" : "paper")}
                className="data-[state=checked]:bg-[#FF3B30]"
              />
              <span className={`text-xs font-[Chivo] tracking-wider ${live ? "text-[#FF3B30]" : "text-neutral-500"}`}>LIVE</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="panic-stop-button"
              onClick={panicStopAll}
              disabled={panicking}
              className="h-8 px-3 bg-[#FF3B30] hover:bg-[#ff5c52] text-white font-[Chivo] tracking-wider text-xs disabled:opacity-60"
            >
              <AlertOctagon size={14} className="mr-1.5" />
              {panicking ? "STOPPING…" : "PANIC STOP"}
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
              <span className="text-xs text-neutral-400">Engine Online</span>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6" data-testid="main-content">
          {children}
        </main>
        <LegalFooter />
      </div>
    </div>
  );
}
